/**
 * Requirements profile and qualified supply — the IO shell for Phase B/C
 * (planning_requirements, planning_resource_capabilities, credentials).
 *
 * The pure matching lives in planningQualifiedCapacity.ts; the pure roster
 * roll-up in planningRoster.ts. This module's only job is to assemble the
 * QualificationInput a run needs, from the substrate that already exists.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  deriveRosterCapacityRows,
  scenarioUsesRoster,
  type PlanningResource,
  type PlanningRoleAssignment,
} from './planningRoster';
import { getRosterSupply } from './planningRosterService';
import { listCapabilities } from './capabilityService';
import type {
  CapacityModelRow,
  PlanningRequirement,
  QualificationInput,
  QualifiedSupplyUnit,
  RequirementDimension,
  Scenario,
} from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// Requirements (demand side)
// ---------------------------------------------------------------------------

const TABLE_REQUIREMENTS = 'planning_requirements';

/**
 * Every requirement in the workspace, as stored.
 *
 * Names are resolved separately by `attachRequirementNames`, not by an embedded
 * select: PostgREST needs an explicit FK hint to disambiguate the two
 * planning_resources relationships on this table, and hard-coding a constraint
 * name into a query is exactly the kind of coupling that breaks silently the
 * next time somebody renames one. Every caller already holds the catalogs.
 */
export async function getPlanningRequirements(
  workspaceId: string,
): Promise<PlanningRequirement[]> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_REQUIREMENTS)
    .select(
      'id, priority_id, work_unit_id, capability_id, min_level, named_resource_id, credential_id',
    )
    .eq('workspace_id', workspaceId);
  if (error) throw error;

  return (data ?? []).map((raw): PlanningRequirement => {
    const row = raw as Record<string, unknown>;
    const dimension: RequirementDimension = row.named_resource_id
      ? 'named_resource'
      : row.credential_id
        ? 'credential'
        : 'capability';
    return {
      id: row.id as string,
      priorityId: (row.priority_id as string | null) ?? null,
      workUnitId: (row.work_unit_id as string | null) ?? null,
      dimension,
      capabilityId: (row.capability_id as string | null) ?? null,
      capabilityName: null,
      minLevel: (row.min_level as number | null) ?? null,
      credentialId: (row.credential_id as string | null) ?? null,
      credentialName: null,
      namedResourceId: (row.named_resource_id as string | null) ?? null,
      namedResourceName: null,
    };
  });
}

/**
 * Fills in the human names an exclusion reason has to say out loud.
 *
 * An unresolved name stays null rather than becoming an id: a reason that reads
 * "Requires 7f3a-… at level 6" is worse than one that says "a capability".
 */
export function attachRequirementNames(
  requirements: PlanningRequirement[],
  catalogs: {
    capabilityNames?: Map<string, string>;
    credentialNames?: Map<string, string>;
    resourceNames?: Map<string, string>;
  },
): PlanningRequirement[] {
  const { capabilityNames, credentialNames, resourceNames } = catalogs;
  return requirements.map((r) => ({
    ...r,
    capabilityName: r.capabilityId ? (capabilityNames?.get(r.capabilityId) ?? null) : null,
    credentialName: r.credentialId ? (credentialNames?.get(r.credentialId) ?? null) : null,
    namedResourceName: r.namedResourceId ? (resourceNames?.get(r.namedResourceId) ?? null) : null,
  }));
}

export async function addPlanningRequirement(params: {
  workspaceId: string;
  priorityId?: string | null;
  workUnitId?: string | null;
  capabilityId?: string | null;
  areaId?: string | null;
  minLevel?: number | null;
  credentialId?: string | null;
  namedResourceId?: string | null;
  notes?: string | null;
}): Promise<{ id: string }> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_REQUIREMENTS)
    .insert({
      workspace_id: params.workspaceId,
      priority_id: params.priorityId ?? null,
      work_unit_id: params.workUnitId ?? null,
      capability_id: params.capabilityId ?? null,
      area_id: params.areaId ?? null,
      min_level: params.minLevel ?? null,
      credential_id: params.credentialId ?? null,
      named_resource_id: params.namedResourceId ?? null,
      notes: params.notes ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data as { id: string };
}

export async function removePlanningRequirement(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from(TABLE_REQUIREMENTS).delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Qualified supply
// ---------------------------------------------------------------------------

/** capability proficiencies for resources that are NOT app users (mig 688). */
async function getResourceCapabilities(
  workspaceId: string,
): Promise<Map<string, Record<string, number>>> {
  const { data, error } = await getSupabaseClient()
    .from('planning_resource_capabilities')
    .select('resource_id, capability_id, level')
    .eq('workspace_id', workspaceId);
  if (error) throw error;

  const byResource = new Map<string, Record<string, number>>();
  for (const row of (data ?? []) as Array<{
    resource_id: string;
    capability_id: string;
    level: number;
  }>) {
    const held = byResource.get(row.resource_id) ?? {};
    // Highest level wins across areas: a per-area proficiency still means the
    // person can do the thing, and area-scoped matching is future work.
    held[row.capability_id] = Math.max(held[row.capability_id] ?? 0, row.level);
    byResource.set(row.resource_id, held);
  }
  return byResource;
}

/**
 * Capability proficiencies for app users — the one skills model (mig 074).
 *
 * Read unscoped rather than joined to a workspace: `individual_capabilities` is
 * keyed by user, not workspace (membership lives in workspace_memberships), so
 * there is no workspace column to filter on. Narrowing happens where it
 * belongs — `buildSupplyUnits` only ever looks up the users this workspace's
 * roster actually names.
 */
async function getUserCapabilities(): Promise<Map<string, Record<string, number>>> {
  const { data, error } = await getSupabaseClient()
    .from('individual_capabilities')
    .select('user_id, capability_id, level');
  if (error) throw error;

  const byUser = new Map<string, Record<string, number>>();
  for (const row of (data ?? []) as Array<{
    user_id: string;
    capability_id: string;
    level: number;
  }>) {
    const held = byUser.get(row.user_id) ?? {};
    held[row.capability_id] = Math.max(held[row.capability_id] ?? 0, row.level);
    byUser.set(row.user_id, held);
  }
  return byUser;
}

/** Credentials held per resource, with expiry (mig 689). */
async function getResourceCredentials(
  workspaceId: string,
): Promise<Map<string, Record<string, string | null>>> {
  const { data, error } = await getSupabaseClient()
    .from('individual_credentials')
    .select('resource_id, credential_id, expires_at')
    .eq('workspace_id', workspaceId);
  if (error) throw error;

  const byResource = new Map<string, Record<string, string | null>>();
  for (const row of (data ?? []) as Array<{
    resource_id: string;
    credential_id: string;
    expires_at: string | null;
  }>) {
    const held = byResource.get(row.resource_id) ?? {};
    held[row.credential_id] = row.expires_at;
    byResource.set(row.resource_id, held);
  }
  return byResource;
}

/**
 * Turns a scenario's supply into per-unit atoms the ledger can check.
 *
 * One unit per role assignment that contributes to a pool, plus one
 * unattributable unit per manual capacity row — hours nobody stands behind,
 * which fund requirement-free work and nothing else.
 */
export function buildSupplyUnits(args: {
  scenario: Scenario;
  resources: PlanningResource[];
  assignments: PlanningRoleAssignment[];
  /** planning_capacity rows — work-unit grain. */
  manualWorkUnitRows: CapacityModelRow[];
  /** planning_priority_capacity rows — priority grain. */
  manualPriorityRows: CapacityModelRow[];
  capabilitiesByResourceId: Map<string, Record<string, number>>;
  capabilitiesByUserId: Map<string, Record<string, number>>;
  credentialsByResourceId: Map<string, Record<string, string | null>>;
  today?: Date;
}): QualifiedSupplyUnit[] {
  const {
    scenario,
    resources,
    assignments,
    manualWorkUnitRows,
    manualPriorityRows,
    capabilitiesByResourceId,
    capabilitiesByUserId,
    credentialsByResourceId,
    today,
  } = args;

  const resourceById = new Map(resources.map((r) => [r.id, r]));

  // Reuse the roll-up so a unit's work-unit capacity is derived exactly the way
  // the pool total is — one pro-rating rule, not two that can drift apart.
  const pools = deriveRosterCapacityRows({
    scenario,
    resources,
    assignments,
    today,
    includeDedicated: true,
  });

  const units: QualifiedSupplyUnit[] = [];
  for (const pool of pools) {
    for (const contribution of pool.contributors) {
      const resource = resourceById.get(contribution.resourceId);
      const fromResource = capabilitiesByResourceId.get(contribution.resourceId) ?? {};
      const fromUser = resource?.userId ? (capabilitiesByUserId.get(resource.userId) ?? {}) : {};
      units.push({
        id: contribution.assignmentId,
        resourceId: contribution.resourceId,
        resourceName: contribution.displayName,
        resourceType: pool.resourceType,
        workUnitCapacity:
          scenario.defaultWorkUnitHours > 0
            ? (contribution.grossHours * (1 - scenario.capacityBufferPercent)) /
              scenario.defaultWorkUnitHours
            : 0,
        // A person's own proficiencies win over anything recorded against their
        // planning-side row, which only exists for non-user resources anyway.
        capabilityLevels: { ...fromResource, ...fromUser },
        credentials: credentialsByResourceId.get(contribution.resourceId) ?? {},
        attributable: true,
        // The same person backs both grains, so a Hybrid run makes work units
        // and undecomposed priorities compete for their hours.
        grain: 'any',
        dedicatedToPriorityId: contribution.dedicatedToPriorityId,
      });
    }
  }

  const addManual = (rows: CapacityModelRow[], grain: 'work_unit' | 'priority') => {
    for (const row of rows) {
      if (row.source === 'roster') continue; // already expanded above
      units.push({
        id: `manual:${grain}:${row.id}`,
        resourceId: null,
        resourceName: row.resourceName ?? 'Manual pool',
        resourceType: row.resourceType,
        workUnitCapacity: row.workUnitCapacity,
        capabilityLevels: {},
        credentials: {},
        attributable: false,
        grain,
        dedicatedToPriorityId: null,
      });
    }
  };
  addManual(manualWorkUnitRows, 'work_unit');
  addManual(manualPriorityRows, 'priority');

  return units;
}

/**
 * The whole qualification input for a run, or null to leave it on anonymous
 * pools.
 *
 * Null whenever the scenario has not opted into the roster: rough-cut is a
 * supported mode forever, and paying for these reads on every manual run would
 * be a tax on the common case.
 */
export async function buildQualificationInput(args: {
  scenario: Scenario;
  manualWorkUnitRows: CapacityModelRow[];
  manualPriorityRows: CapacityModelRow[];
}): Promise<QualificationInput | null> {
  const { scenario, manualWorkUnitRows, manualPriorityRows } = args;
  if (!scenarioUsesRoster(scenario)) return null;

  try {
    const [
      { resources, assignments },
      rawRequirements,
      resourceCaps,
      userCaps,
      credentials,
      capabilityCatalog,
      credentialCatalog,
    ] = await Promise.all([
      getRosterSupply(scenario.projectId),
      getPlanningRequirements(scenario.projectId),
      getResourceCapabilities(scenario.projectId).catch(() => new Map()),
      getUserCapabilities().catch(() => new Map()),
      getResourceCredentials(scenario.projectId).catch(() => new Map()),
      listCapabilities(scenario.projectId).catch(() => ({ data: [], error: null })),
      listCredentials(scenario.projectId).catch(() => [] as Credential[]),
    ]);

    if (resources.length === 0 && rawRequirements.length === 0) return null;

    // Resolved here so an exclusion reason can name the constraint rather than
    // reciting an id at somebody.
    const requirements = attachRequirementNames(rawRequirements, {
      capabilityNames: new Map((capabilityCatalog.data ?? []).map((c) => [c.id, c.name])),
      credentialNames: new Map(credentialCatalog.map((c) => [c.id, c.name])),
      resourceNames: new Map(resources.map((r) => [r.id, r.displayName])),
    });

    return {
      supply: buildSupplyUnits({
        scenario,
        resources,
        assignments,
        manualWorkUnitRows,
        manualPriorityRows,
        capabilitiesByResourceId: resourceCaps,
        capabilitiesByUserId: userCaps,
        credentialsByResourceId: credentials,
      }),
      requirements,
      horizonEndDate: scenario.horizonEndDate,
    };
  } catch (err) {
    // Qualification is an enrichment. Losing it must degrade the run to
    // anonymous pools, never break it.
    console.warn('[buildQualificationInput] falling back to anonymous pools', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Credentials (Phase C) — binary eligibility gates with an expiry
// ---------------------------------------------------------------------------

export interface Credential {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  active: boolean;
}

export interface IndividualCredential {
  id: string;
  resourceId: string;
  credentialId: string;
  attainedAt: string | null;
  expiresAt: string | null;
}

export async function listCredentials(workspaceId: string): Promise<Credential[]> {
  const { data, error } = await getSupabaseClient()
    .from('credentials')
    .select('id, workspace_id, name, description, active')
    .eq('workspace_id', workspaceId)
    .eq('active', true)
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    active: (row.active as boolean) ?? true,
  }));
}

export async function createCredential(params: {
  workspaceId: string;
  name: string;
  description?: string | null;
}): Promise<Credential> {
  const { data, error } = await getSupabaseClient()
    .from('credentials')
    .insert({
      workspace_id: params.workspaceId,
      name: params.name.trim(),
      description: params.description ?? null,
    })
    .select('id, workspace_id, name, description, active')
    .single();
  if (error) throw error;
  const row = data as Record<string, unknown>;
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    active: true,
  };
}

export async function listIndividualCredentials(
  workspaceId: string,
): Promise<IndividualCredential[]> {
  const { data, error } = await getSupabaseClient()
    .from('individual_credentials')
    .select('id, resource_id, credential_id, attained_at, expires_at')
    .eq('workspace_id', workspaceId);
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    resourceId: row.resource_id as string,
    credentialId: row.credential_id as string,
    attainedAt: (row.attained_at as string | null) ?? null,
    expiresAt: (row.expires_at as string | null) ?? null,
  }));
}

export async function grantCredential(params: {
  workspaceId: string;
  resourceId: string;
  credentialId: string;
  attainedAt?: string | null;
  expiresAt?: string | null;
}): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('individual_credentials')
    .upsert(
      {
        workspace_id: params.workspaceId,
        resource_id: params.resourceId,
        credential_id: params.credentialId,
        attained_at: params.attainedAt ?? null,
        expires_at: params.expiresAt ?? null,
      },
      { onConflict: 'resource_id,credential_id' },
    );
  if (error) throw error;
}

export async function revokeCredential(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from('individual_credentials').delete().eq('id', id);
  if (error) throw error;
}
